import { ChangeEvent, useState } from 'react'

import { Button } from '@it-incubator/ui-kit'
import { Grid, InputLabel, MenuItem, TextField } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { CustomModal } from 'common/components/custom-modal/CustomModal'
import { Playlist, PlaylistType, Setting } from 'features/playlists/service'
import moment from 'moment'
import { CourseTypes, useGetCoursesQuery } from 'services/courses'
import { capitalizeFirstLetter } from 'utils'

import { PlaylistSettings } from './playlist-settings/PlaylistSettings'

type Props = {
  callback: (playlistModel: Playlist) => void
  close: () => void
  defName?: string
  defSlug?: string
  defDescription?: string
  defSettings?: Setting[]
  defSeoDescription?: string
  show: boolean
}

export const PlaylistModal = ({
  callback,
  close,
  defName,
  defSettings,
  defSlug,
  defDescription,
  defSeoDescription,
  show,
}: Props) => {
  const initialSetting = defSettings?.[0]
  const [name, setName] = useState(defName || '')
  const [description, setDescription] = useState(defDescription || '')
  const [seoDescription, setSeoDescription] = useState(defSeoDescription || '')
  const [slug, setSlug] = useState(defSlug || '')
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(
    initialSetting?.courseId ?? null
  )
  const [settings, setSettings] = useState<Setting[]>(initialSetting ? [initialSetting] : [])
  const [error, setError] = useState(false)
  const [errorSlug, setErrorSlug] = useState(false)
  const [errorDescription, setErrorDescription] = useState(false)
  const [errorSeoDescription, setErrorSeoDescription] = useState(false)

  const { data: courses } = useGetCoursesQuery()

  const handleCourseChange = (event: SelectChangeEvent<number>) => {
    const newCourseId = event.target.value as number

    if (newCourseId === selectedCourseId) {
      return
    }

    setSelectedCourseId(newCourseId)
    setSettings([
      {
        accessLevel: 0,
        courseId: newCourseId,
        // Границы окна видимости по умолчанию отсутствуют (null).
        maxStudentStartCourseDate: null,
        minStudentStartCourseDate: null,
        plType: '',
      },
    ])
  }

  const changeName = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    const maxLengthValue = 250

    if (value.length > maxLengthValue) {
      setError(true)
    } else {
      setName(value)
      setError(false)
    }
  }

  const changeSlug = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    const maxLengthValue = 50

    setSlug(value)

    if (value.length > maxLengthValue) {
      setErrorSlug(true)
    } else {
      setErrorSlug(false)
      setSlug(value)
    }
  }

  const changeDescription = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    const maxLengthValue = 100

    if (value.length > maxLengthValue) {
      setErrorDescription(true)
    } else {
      setErrorDescription(false)
      setDescription(value)
    }
  }

  const changeSeoDescription = (e: ChangeEvent<HTMLInputElement>) => {
    setSeoDescription(e.currentTarget.value)
  }

  const changeLevelAccess = (event: SelectChangeEvent<number>) => {
    setSettings(settings.map(setting => ({ ...setting, accessLevel: +event.target.value })))
  }

  const changeStartDate = (event: null | number) => {
    setSettings(
      settings.map(setting => ({
        ...setting,
        // Очистка пикера → null (без границы), а не NaN/1970.
        minStudentStartCourseDate: event ? moment(event).unix() * 1000 : null,
      }))
    )
  }

  const changeEndDate = (event: null | number) => {
    setSettings(
      settings.map(setting => ({
        ...setting,
        maxStudentStartCourseDate: event ? moment(event).unix() * 1000 : null,
      }))
    )
  }

  const changePlaylistType = (event: SelectChangeEvent<PlaylistType>) => {
    setSettings(
      settings.map(setting => ({
        ...setting,
        plType: event.target.value as PlaylistType,
      }))
    )
  }

  const onSave = () => {
    if (!selectedCourseId) {
      return
    }

    const course = courses?.items.find(c => c.id === selectedCourseId)

    if (course?.type === CourseTypes.Public && !slug) {
      setErrorSlug(true)

      return
    }

    callback({
      name: name,
      settings: settings,
      ...(slug && { slug }),
      ...(description && { description }),
      ...(seoDescription && { seoDescription }),
    })

    if (!defName && !defSettings) {
      setSettings([])
      setSelectedCourseId(null)
      setName('')
    }
  }

  const currentSetting = settings[0]

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <CustomModal
        modalTitle={defName && defSettings ? 'Редактировать плейлист' : 'Новый плейлист'}
        onClose={close}
        open={show}
      >
        <Grid marginBottom={2}>
          <TextField
            error={error}
            fullWidth
            helperText={error && 'Максимальная длина 250 символов'}
            label={'Название плейлиста'}
            onChange={changeName}
            value={name}
          />
        </Grid>

        <Grid marginBottom={2}>
          <TextField
            error={errorSlug}
            fullWidth
            helperText={errorSlug && 'Максимальная длина 50 символов'}
            label={'Slug'}
            onChange={changeSlug}
            value={slug}
          />
        </Grid>

        <Grid marginBottom={2}>
          <TextField
            multiline
            error={errorDescription}
            fullWidth
            helperText={errorDescription && 'Максимальная длина 100 символов'}
            label={'Описание плейлиста'}
            onChange={changeDescription}
            value={description}
          />
        </Grid>

        <Grid marginBottom={2}>
          <TextField
            multiline
            error={errorSeoDescription}
            fullWidth
            helperText={errorSeoDescription && 'Максимальная длина 100 символов'}
            label={'SEO описание'}
            onChange={changeSeoDescription}
            value={seoDescription}
          />
        </Grid>

        <Grid marginBottom={2}>
          <FormControl fullWidth variant={'outlined'}>
            <InputLabel>Курс *</InputLabel>
            <Select
              label={'Курс *'}
              onChange={handleCourseChange}
              required
              value={selectedCourseId || ''}
            >
              {courses?.items.map(course => (
                <MenuItem key={course.id} value={course.id}>
                  {capitalizeFirstLetter(course.title)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {currentSetting && (
          <PlaylistSettings
            changeEndDate={changeEndDate}
            changeLevelAccess={changeLevelAccess}
            changePlaylistType={changePlaylistType}
            changeStartDate={changeStartDate}
            endDate={currentSetting.maxStudentStartCourseDate}
            levelAccess={currentSetting.accessLevel}
            playlistType={currentSetting.plType}
            startDate={currentSetting.minStudentStartCourseDate}
          />
        )}

        <Grid container justifyContent={'flex-end'} marginTop={4}>
          <Button
            disabled={
              !name ||
              !selectedCourseId ||
              error ||
              errorSlug ||
              errorDescription ||
              errorSeoDescription
            }
            onClick={onSave}
          >
            Сохранить
          </Button>
        </Grid>
      </CustomModal>
    </LocalizationProvider>
  )
}
